import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Search, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, IndianRupee
} from "lucide-react";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 2,
  }).format(Number(n || 0));

const statusClass = (s) => ({
  active: "text-emerald-400 bg-emerald-400/10",
  completed: "text-blue-400 bg-blue-400/10",
  pending: "text-amber-400 bg-amber-400/10",
  approved: "text-emerald-400 bg-emerald-400/10",
  rejected: "text-red-400 bg-red-400/10",
  cancelled: "text-white/50 bg-white/5",
}[s] || "text-white/60 bg-white/5");

export default function Mandates() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [proofUrls, setProofUrls] = useState({});
  const [form, setForm] = useState({
    title: "", description: "", category: "",
    customer_name: "", customer_email: "", customer_phone: "",
    total_amount: "", initial_percent: 35, minimum_installment: 1,
    allowed_methods: ["manual_upi", "razorpay"], upi_id: "",
    due_date: "", logo_url: "", terms: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/mandates", {
        params: { q: query || undefined, limit: 300 },
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load mandates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/admin/branches")
      .then(({ data }) => setCategories(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.error("Could not load mandate categories", e);
        toast.error("Could not load categories");
      });
  }, []);

  const openMandate = async (m) => {
    setSelected(m);
    setInstallments([]);
    try {
      const { data } = await api.get(`/admin/mandates/${m.mandate_id}/installments`);
      setInstallments(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load installments");
    }
  };

  const createMandate = async (e) => {
    e.preventDefault();
    if (form.allowed_methods.includes("manual_upi") && !form.upi_id.trim()) {
      toast.error("UPI ID is required when Manual UPI is enabled");
      return;
    }
    try {
      const payload = {
        ...form,
        total_amount: Number(form.total_amount),
        initial_percent: Number(form.initial_percent),
        minimum_installment: Number(form.minimum_installment),
        due_date: form.due_date || null,
      };
      const { data } = await api.post("/admin/mandates", payload);
      toast.success(`Mandate created: ${data.mandate_id}`);
      setShowCreate(false);
      setForm({
        title: "", description: "", category: "",
        customer_name: "", customer_email: "", customer_phone: "",
        total_amount: "", initial_percent: 35, minimum_installment: 1,
        allowed_methods: ["manual_upi", "razorpay"], upi_id: "",
        due_date: "", logo_url: "", terms: "",
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mandate creation failed");
    }
  };


  const viewProof = async (item) => {
    try {
      const { data } = await api.get(
        `/admin/mandates/${item.mandate_id}/installments/${item.installment_id}/screenshot`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      setProofUrls((prev) => {
        if (prev[item.installment_id]) URL.revokeObjectURL(prev[item.installment_id]);
        return { ...prev, [item.installment_id]: url };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load payment screenshot");
    }
  };

  const review = async (item, action) => {
    const reason = action === "reject"
      ? window.prompt("Reason for rejection (optional):") || ""
      : "";
    if (action === "reject" && reason === null) return;

    setBusyId(item.installment_id);
    try {
      await api.post(
        `/admin/mandates/${item.mandate_id}/installments/${item.installment_id}/${action}`,
        { reason }
      );
      toast.success(action === "approve" ? "Installment approved" : "Installment rejected");
      await load();
      if (selected) await openMandate(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action} installment`);
    } finally {
      setBusyId("");
    }
  };

  const toggleMethod = (method) => {
    setForm((f) => ({
      ...f,
      allowed_methods: f.allowed_methods.includes(method)
        ? f.allowed_methods.filter((x) => x !== method)
        : [...f.allowed_methods, method],
    }));
  };

  const field = "w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#fbce18]/60";
  const label = "block text-xs text-white/50 mb-1.5";

  return (
    <div className="space-y-6 text-white" data-testid="admin-mandates-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Mandates</h1>
          <p className="text-sm text-white/45 mt-1">Installment-based customer payments</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="rounded-xl border border-white/10 p-2.5 hover:bg-white/5" title="Refresh">
            <RefreshCw size={17} className={loading ? "animate-spin" : ""}/>
          </button>
          <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 rounded-xl bg-[#fbce18] px-4 py-2.5 text-sm font-semibold text-black">
            <Plus size={17}/> Create mandate
          </button>
        </div>
      </div>

      {showCreate && (
        <form onSubmit={createMandate} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
          <h2 className="font-semibold">Create new mandate</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div><label className={label}>Title *</label><input className={field} required minLength={2} value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/></div>
            <div>
              <label className={label}>Category *</label>
              <select
                className={field}
                required
                value={form.category}
                onChange={e => setForm({...form, category: e.target.value})}
              >
                <option value="">Select category</option>
                {categories.map(category => (
                  <option key={category.id || category.slug || category.name} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div><label className={label}>Customer name *</label><input className={field} required value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})}/></div>
            <div><label className={label}>Customer email *</label><input className={field} type="email" required value={form.customer_email} onChange={e=>setForm({...form,customer_email:e.target.value})}/></div>
            <div><label className={label}>Customer phone</label><input className={field} value={form.customer_phone} onChange={e=>setForm({...form,customer_phone:e.target.value})}/></div>
            <div><label className={label}>Total amount (₹) *</label><input className={field} type="number" min="0.01" step="0.01" required value={form.total_amount} onChange={e=>setForm({...form,total_amount:e.target.value})}/></div>
            <div><label className={label}>Initial installment (%)</label><input className={field} type="number" min="1" max="100" required value={form.initial_percent} onChange={e=>setForm({...form,initial_percent:e.target.value})}/></div>
            <div><label className={label}>Minimum installment (₹)</label><input className={field} type="number" min="0.01" step="0.01" required value={form.minimum_installment} onChange={e=>setForm({...form,minimum_installment:e.target.value})}/></div>
            <div><label className={label}>UPI ID</label><input className={field} placeholder="name@bank" value={form.upi_id} onChange={e=>setForm({...form,upi_id:e.target.value})}/></div>
            <div><label className={label}>Due date</label><input className={field} type="date" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})}/></div>
          </div>
          <div>
            <label className={label}>Payment methods</label>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.allowed_methods.includes("manual_upi")} onChange={()=>toggleMethod("manual_upi")}/> Manual UPI (UTR review)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.allowed_methods.includes("razorpay")} onChange={()=>toggleMethod("razorpay")}/> Razorpay</label>
            </div>
          </div>
          <div><label className={label}>Description</label><textarea className={field} rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
          <div><label className={label}>Terms</label><textarea className={field} rows={2} value={form.terms} onChange={e=>setForm({...form,terms:e.target.value})}/></div>
          <div className="flex gap-2">
            <button className="rounded-xl bg-[#fbce18] px-4 py-2.5 text-sm font-semibold text-black">Create mandate</button>
            <button type="button" onClick={()=>setShowCreate(false)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="flex gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3">
          <Search size={16} className="text-white/40"/>
          <input className="w-full bg-transparent py-3 text-sm outline-none" placeholder="Search title, customer, email or mandate ID" value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()}/>
        </div>
        <button onClick={load} className="rounded-xl border border-white/10 px-4 text-sm">Search</button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10">
        <div className="grid grid-cols-1 gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/45 md:grid-cols-5">
          <span>Mandate / Customer</span><span>Total</span><span>Paid / Remaining</span><span>Status</span><span>Details</span>
        </div>
        {rows.map(m=>(
          <div key={m.mandate_id} className="grid grid-cols-1 gap-2 border-b border-white/5 px-4 py-4 text-sm last:border-0 md:grid-cols-5 md:items-center">
            <div><div className="font-medium">{m.title}</div><div className="text-xs text-white/45">{m.customer_name} · {m.customer_email}</div><div className="text-[11px] text-white/30">{m.mandate_id}</div></div>
            <div>{money(m.total_amount)}</div>
            <div><div>{money(m.paid_amount)} paid</div><div className="text-xs text-white/45">{money(m.remaining_amount)} remaining</div></div>
            <div><span className={`rounded-full px-2 py-1 text-xs ${statusClass(m.status)}`}>{m.status}</span></div>
            <div><button onClick={()=>openMandate(m)} className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs hover:bg-white/5">Installments <ChevronDown size={14}/></button></div>
          </div>
        ))}
        {!rows.length && <div className="p-8 text-center text-sm text-white/40">{loading ? "Loading…" : "No mandates found."}</div>}
      </div>

      {selected && (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div><h2 className="font-semibold">{selected.title} — Installments</h2><p className="text-xs text-white/40">{selected.mandate_id}</p></div>
            <button onClick={()=>setSelected(null)} className="text-sm text-white/50 hover:text-white">Close</button>
          </div>
          <div className="space-y-3">
            {installments.map(item=>(
              <div key={item.installment_id} className="rounded-xl border border-white/10 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">{money(item.amount)} · {item.method}</div>
                    <div className="text-xs text-white/40">{item.installment_id} · {item.created_at ? new Date(item.created_at).toLocaleString() : ""}</div>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs ${statusClass(item.status)}`}>{item.status}</span>
                </div>
                {item.invoice_no && <div className="mt-2 text-xs text-white/50">Invoice: {item.invoice_no}</div>}
                <div className="mt-2 grid gap-1 text-xs text-white/60 sm:grid-cols-2">
                  <div><span className="text-white/35">Mandate:</span> {selected.mandate_id}</div>
                  <div><span className="text-white/35">Customer:</span> {selected.customer_name || selected.name || selected.customer_email || selected.email || "—"}</div>
                  <div><span className="text-white/35">Payment method:</span> {item.method || "—"}</div>
                  <div><span className="text-white/35">Submitted:</span> {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : item.created_at ? new Date(item.created_at).toLocaleString() : "—"}</div>
                  <div><span className="text-white/35">Amount:</span> {money(item.amount)}</div>
                  <div><span className="text-white/35">Status:</span> {item.status || "—"}</div>
                </div>
                {item.customer_note && <div className="mt-1 text-sm text-white/60">Note: {item.customer_note}</div>}
                {item.method==="manual_upi" && (
                  <div className="mt-3">
                    <button type="button" onClick={()=>viewProof(item)}
                      className="rounded-lg border border-white/10 px-3 py-2 text-xs text-[#fbce18]">
                      View payment screenshot
                    </button>
                    {proofUrls[item.installment_id] && (
                      <img src={proofUrls[item.installment_id]} alt="Customer payment proof"
                        className="mt-3 max-h-[520px] max-w-full rounded-lg border border-white/10 object-contain"/>
                    )}
                  </div>
                )}

                {item.rejection_reason && <div className="mt-1 text-sm text-red-300">Reason: {item.rejection_reason}</div>}
                {item.status==="pending" && item.method==="manual_upi" && (
                  <div className="mt-3 flex gap-2">
                    <button disabled={busyId===item.installment_id} onClick={()=>review(item,"approve")} className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-3 py-2 text-xs text-emerald-300 disabled:opacity-50"><CheckCircle size={14}/> Approve</button>
                    <button disabled={busyId===item.installment_id} onClick={()=>review(item,"reject")} className="flex items-center gap-1 rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-300 disabled:opacity-50"><XCircle size={14}/> Reject</button>
                  </div>
                )}
              </div>
            ))}
            {!installments.length && <div className="text-sm text-white/40">No installments submitted yet.</div>}
          </div>
        </section>
      )}
    </div>
  );
}
